import { signIn, signOut, useSession } from 'next-auth/react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import { FIELD_TYPES, STRINGS } from '../../../constants';
import { fetchProfileDetails, updateProfile } from '../../../services/settings';
import { getProfile } from '../../../store/actions';
import ProfileScreen from '../../scenes/settings/profile/ProfileScreen';

const ProfileScreenContainer = () => {
  const { data: session, status } = useSession();
  const profile = useSelector((state) => state.profile);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [editIsLoading, setEditIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm({ reValidateMode: 'onSubmit' });

  useEffect(() => reset(), [isModalVisible]);

  const loadProfileData = async () => {
    try {
      await refreshProfile();
      setIsLoading(false);
    } catch (err) {
      setError(err);
    }
  };

  useEffect(() => {
    if (session?.error === 'RefreshAccessTokenError' || status === 'unauthenticated') {
      signIn(); // Force sign in to hopefully resolve error
    }
    if (session?.user && status === 'authenticated') {
      loadProfileData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.error, status]);

  const refreshProfile = async () => {
    await fetchProfileDetails(session?.user?.profileId, setError, saveProfile, logoutHandler);
  };

  const onRefresh = async () => {
    setIsRefreshing(true);
    await refreshProfile();
    setIsRefreshing(false);
  };

  const saveProfile = (data) => {
    if (Object.keys(data).length !== 0) {
      dispatch(getProfile(data));
    }
  };

  const editProfileHandler = handleSubmit(async (value) => {
    setEditIsLoading(true);
    await updateProfile(
      session?.user?.profileId,
      value,
      setIsModalVisible,
      onRefresh,
      setError,
      logoutHandler,
    );
    setEditIsLoading(false);
  });

  const editProfileFields = [
    {
      name: 'slug',
      defaultValue: profile?.slug,
      label: STRINGS.settings.slugText,
      placeholder: STRINGS.settings.slugText,
      type: FIELD_TYPES.INPUT_TEXT,
      fieldError: errors.slug,
      register,
      inputProps: {
        maxLength: 20,
        autoCapitalize: 'none',
        autoCompleteType: 'name',
        textContentType: 'name',
      },
    },
    {
      name: 'name',
      defaultValue: profile?.name,
      label: STRINGS.settings.nameText,
      placeholder: STRINGS.settings.nameText,
      type: FIELD_TYPES.INPUT_TEXT,
      fieldError: errors.name,
      register,
      inputProps: {
        maxLength: 20,
        autoCapitalize: 'words',
        autoCompleteType: 'name',
        textContentType: 'givenName',
      },
    },
    {
      name: 'surname',
      defaultValue: profile?.surname,
      label: STRINGS.settings.surnameText,
      placeholder: STRINGS.settings.surnameText,
      type: FIELD_TYPES.INPUT_TEXT,
      fieldError: errors.surname,
      register,
      inputProps: {
        maxLength: 30,
        autoCapitalize: 'words',
        autoCompleteType: 'name',
        textContentType: 'familyName',
      },
    },
    {
      name: 'phone',
      defaultValue: profile?.phone,
      label: STRINGS.settings.phoneText,
      placeholder: STRINGS.settings.phoneText,
      type: FIELD_TYPES.INPUT_TEXT,
      register,
      validation: {
        minLength: {
          value: 11,
          message: STRINGS.common.invalidNumberFormat,
        },
      },
      fieldError: errors.phone,
      inputProps: {
        maxLength: 15,
        keyboardType: 'numeric',
        autoCompleteType: 'tel',
        textContentType: 'telephoneNumber',
      },
    },
    {
      name: 'bio',
      defaultValue: profile?.bio,
      label: STRINGS.settings.bioText,
      placeholder: STRINGS.settings.bioText,
      type: FIELD_TYPES.INPUT_TEXT_AREA,
      showLabel: true,
      register,
      validation: {
        maxLength: {
          value: 100,
          message: STRINGS.common.maxLength,
        },
      },
      error: errors.bio,
      inputProps: {
        maxLength: 100,
        autoCapitalize: 'sentences',
        multiline: true,
      },
    },
  ];

  const logoutHandler = async () => {
    await signOut();
  };

  return (
    <ProfileScreen
      profile={profile}
      editProfileFields={editProfileFields}
      editProfileHandler={editProfileHandler}
      logoutHandler={logoutHandler}
      setIsModalVisible={setIsModalVisible}
      isModalVisible={isModalVisible}
      editIsLoading={editIsLoading}
      isLoading={isLoading}
      isRefreshing={isRefreshing}
      onRefresh={onRefresh}
      error={error}
      status={status}
    />
  );
};

export default ProfileScreenContainer;
